import { Link, useParams } from "react-router-dom";
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import {
  useStudentQuery,
  useStudentAssessmentsQuery,
  useUpdateStudentMutation,
} from "@/hooks/useStudents";
import { formatDate } from "@/utils/date";
import type { Assessment, Student } from "@/types/assessment";
import type { GuardianFormValue, StudentFormValues } from "@/components/students/StudentFormDialog";
import {
  assessmentEntryMode,
  assessmentRecordLabel,
  entryModeLabel,
  studentRecommendedAction,
  studentWorkspacePaths,
} from "@/utils/student-workspace";

export function StudentDetailPage(): JSX.Element {
  const { studentId } = useParams<{ studentId: string }>();
  const studentQuery = useStudentQuery(studentId);
  const assessmentQuery = useStudentAssessmentsQuery(studentId);
  const updateStudentMutation = useUpdateStudentMutation();
  const [editingInformation, setEditingInformation] = useState(false);
  const [informationValues, setInformationValues] = useState<StudentFormValues | null>(null);
  const [informationError, setInformationError] = useState<string | null>(null);

  useEffect(() => {
    if (studentQuery.data && !editingInformation) {
      setInformationValues(toStudentFormValues(studentQuery.data));
    }
  }, [editingInformation, studentQuery.data]);

  const assessmentsByStatus = useMemo(() => {
    const items = assessmentQuery.data ?? [];
    const completed = items.filter((assessment) => assessment.status === "completed").length;
    return {
      total: items.length,
      completed,
      inProgress: items.length - completed,
    };
  }, [assessmentQuery.data]);

  const sortedAssessments = useMemo(
    () =>
      [...(assessmentQuery.data ?? [])].sort(
        (left, right) => new Date(right.startedAt).getTime() - new Date(left.startedAt).getTime(),
      ),
    [assessmentQuery.data],
  );

  const recommendation = studentId
    ? studentRecommendedAction(sortedAssessments, studentId)
    : studentRecommendedAction([], "");
  const recommendationCopy = getRecommendationCopy(recommendation);
  const recommendationPath = studentId
    ? recommendation.type === "continue_assessment"
      ? studentWorkspacePaths.assessment(
          studentId,
          assessmentEntryMode(recommendation.assessment),
          recommendation.assessment.id,
        )
      : recommendation.type === "view_report"
        ? studentWorkspacePaths.reports(studentId, recommendation.assessment.id)
        : studentWorkspacePaths.assessments(studentId)
    : studentWorkspacePaths.children;

  function startEditingInformation(): void {
    if (!studentQuery.data) return;
    setInformationValues(toStudentFormValues(studentQuery.data));
    setInformationError(null);
    setEditingInformation(true);
  }

  function cancelEditingInformation(): void {
    if (updateStudentMutation.isPending) return;
    setInformationValues(studentQuery.data ? toStudentFormValues(studentQuery.data) : null);
    setInformationError(null);
    setEditingInformation(false);
  }

  function updateInformationField<Key extends keyof StudentFormValues>(
    key: Key,
    value: StudentFormValues[Key],
  ): void {
    setInformationValues((previous) => (previous ? { ...previous, [key]: value } : previous));
  }

  function updateGuardianField<Key extends keyof GuardianFormValue>(
    index: number,
    key: Key,
    value: GuardianFormValue[Key],
  ): void {
    setInformationValues((previous) => {
      if (!previous) return previous;
      return {
        ...previous,
        guardians: previous.guardians.map((guardian, guardianIndex) =>
          guardianIndex === index ? { ...guardian, [key]: value } : guardian,
        ),
      };
    });
  }

  function addGuardian(): void {
    setInformationValues((previous) =>
      previous
        ? { ...previous, guardians: [...previous.guardians, createEmptyGuardian()] }
        : previous,
    );
  }

  function removeGuardian(index: number): void {
    setInformationValues((previous) =>
      previous
        ? {
            ...previous,
            guardians: previous.guardians.filter((_, guardianIndex) => guardianIndex !== index),
          }
        : previous,
    );
  }

  async function saveInformation(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!studentId || !informationValues) return;
    if (!informationValues.name.trim() || !informationValues.birthdate) {
      setInformationError("请填写儿童姓名和出生日期。");
      return;
    }

    setInformationError(null);
    try {
      await updateStudentMutation.mutateAsync({
        studentId,
        payload: toStudentPayload(informationValues),
      });
      setEditingInformation(false);
    } catch (error) {
      setInformationError(error instanceof Error ? error.message : "基本信息保存失败，请重试。");
    }
  }

  return (
    <div className="space-y-6">
      <section className="grid gap-5 lg:grid-cols-[minmax(0,1.5fr)_minmax(18rem,0.7fr)]">
        <div className="rounded-2xl border border-brand/25 bg-gradient-to-br from-brand/[0.09] to-white p-6 shadow-sm">
          <p className="text-xs font-semibold text-brand">建议下一步</p>
          <h3 className="mt-2 text-xl font-semibold text-slate-800">{recommendationCopy.title}</h3>
          <p className="mt-2 max-w-3xl text-sm text-slate-600">{recommendationCopy.description}</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to={recommendationPath}
              className="inline-flex min-h-11 items-center justify-center rounded-lg bg-brand px-5 text-sm font-semibold text-white hover:bg-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
            >
              {recommendationCopy.actionLabel}
            </Link>
            {studentId && sortedAssessments.length > 0 ? (
              <Link
                to={
                  recommendation.type === "continue_assessment"
                    ? studentWorkspacePaths.reports(studentId)
                    : studentWorkspacePaths.assessments(studentId)
                }
                className="inline-flex min-h-11 items-center justify-center rounded-lg border border-brand/30 bg-white px-5 text-sm font-semibold text-brand-dark hover:bg-brand/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
              >
                {recommendation.type === "continue_assessment" ? "查看已有报告" : "查看评估记录"}
              </Link>
            ) : null}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-base font-semibold text-slate-800">记录概况</h3>
          <dl className="mt-4 space-y-3 text-sm">
            <StatusRow label="评估记录" value={`${assessmentsByStatus.total} 次`} />
            <StatusRow label="进行中" value={`${assessmentsByStatus.inProgress} 次`} />
            <StatusRow label="已完成" value={`${assessmentsByStatus.completed} 次`} />
          </dl>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        {studentQuery.isLoading ? (
          <p className="text-sm text-slate-500">正在加载儿童信息…</p>
        ) : null}
        {studentQuery.isError ? (
          <p className="text-sm text-red-500">儿童信息加载失败，请稍后重试。</p>
        ) : null}
        {!studentQuery.isLoading && !studentQuery.isError && !studentQuery.data ? (
          <p className="text-sm text-red-500">未找到该儿童，请返回列表重新选择。</p>
        ) : null}
        {studentQuery.data && informationValues ? (
          <form id="student-information-form" className="space-y-6" onSubmit={saveInformation}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">基本信息</h2>
                <p className="mt-1 text-sm text-slate-500">
                  儿童、就读和监护人信息均保存在当前账号所属学校。
                </p>
              </div>
              <div className="flex items-center gap-2">
                {editingInformation ? (
                  <>
                    <button
                      type="button"
                      onClick={cancelEditingInformation}
                      disabled={updateStudentMutation.isPending}
                      className="inline-flex min-h-11 items-center justify-center rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      取消
                    </button>
                    <button
                      type="submit"
                      disabled={updateStudentMutation.isPending}
                      className="inline-flex min-h-11 items-center justify-center rounded-lg bg-brand px-5 text-sm font-semibold text-white shadow-sm hover:bg-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
                    >
                      {updateStudentMutation.isPending ? "保存中…" : "保存"}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={startEditingInformation}
                    className="inline-flex min-h-11 items-center justify-center rounded-lg bg-brand px-5 text-sm font-semibold text-white shadow-sm hover:bg-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
                  >
                    编辑
                  </button>
                )}
              </div>
            </div>

            {informationError ? (
              <p
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                {informationError}
              </p>
            ) : null}

            {editingInformation ? (
              <StudentInformationEditor
                values={informationValues}
                onFieldChange={updateInformationField}
                onGuardianChange={updateGuardianField}
                onAddGuardian={addGuardian}
                onRemoveGuardian={removeGuardian}
              />
            ) : (
              <StudentInformationDetails student={studentQuery.data} />
            )}
          </form>
        ) : null}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">近期评估记录</h2>
          </div>
          <div className="flex gap-2 text-xs text-slate-500">
            <Tag>总计 {assessmentsByStatus.total}</Tag>
            <Tag type="success">已完成 {assessmentsByStatus.completed}</Tag>
            <Tag type="progress">进行中 {assessmentsByStatus.inProgress}</Tag>
          </div>
        </div>

        {assessmentQuery.isLoading ? (
          <p className="mt-4 text-sm text-slate-500">正在加载评估记录…</p>
        ) : null}
        {assessmentQuery.isError ? (
          <p className="mt-4 text-sm text-red-500">评估记录加载失败，请稍后重试。</p>
        ) : null}

        {assessmentQuery.data && assessmentQuery.data.length > 0 ? (
          <ul className="mt-6 space-y-4">
            {sortedAssessments.slice(0, 5).map((assessment) => (
              <li
                key={assessment.id}
                className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 md:flex-row md:items-center md:justify-between"
              >
                <div>
                  <p className="font-semibold text-slate-700">
                    {assessmentRecordLabel()} · {formatDate(assessment.startedAt)}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    状态：{renderAssessmentStatus(assessment.status)} · 评估师：
                    {assessment.evaluatorDisplayName ?? "历史评估师"}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Tag>完成条目 {completedEntryCount(assessment)}</Tag>
                  {studentId ? (
                    <Link
                      to={
                        assessment.status === "completed"
                          ? studentWorkspacePaths.reports(studentId, assessment.id)
                          : studentWorkspacePaths.assessment(
                              studentId,
                              assessmentEntryMode(assessment),
                              assessment.id,
                            )
                      }
                      className="inline-flex min-h-11 items-center rounded-lg border border-slate-300 px-3 text-xs font-semibold text-slate-600 hover:border-brand/40 hover:bg-brand/5 hover:text-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
                    >
                      {assessment.status === "completed" ? "查看结果" : "继续评估"}
                    </Link>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : null}

        {assessmentQuery.data && assessmentQuery.data.length === 0 ? (
          <p className="mt-4 text-sm text-slate-500">
            暂未记录评估，可从“评估”页选择一种方式开始。
          </p>
        ) : null}
      </section>
    </div>
  );
}

function StudentInformationDetails({ student }: { student: Student }): JSX.Element {
  const disabilityCategory = getMetadataString(student.metadata, "disabilityCategory");
  const disabilityCause = getMetadataString(student.metadata, "disabilityCause");
  const disabilityLevel = getMetadataString(student.metadata, "disabilityLevel");
  const notes = getMetadataString(student.metadata, "notes");

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <InfoBlock title="姓名" value={student.profile.name} />
        <InfoBlock title="性别" value={renderGender(student.profile.gender)} />
        <InfoBlock
          title="年龄"
          value={student.profile.age == null ? "未填写" : `${student.profile.age} 岁`}
        />
        <InfoBlock title="出生日期" value={displayDate(student.profile.birthdate)} />
        <InfoBlock title="学校" value={displayValue(student.enrollment.school)} />
        <InfoBlock title="班级" value={displayValue(student.enrollment.classroom)} />
        <InfoBlock title="小组" value={displayValue(student.enrollment.group)} />
        <InfoBlock title="入学时间" value={displayDate(student.enrollment.startDate)} />
        <InfoBlock title="障碍类别" value={displayValue(disabilityCategory)} />
        <InfoBlock title="障碍原因" value={displayValue(disabilityCause)} />
        <InfoBlock title="障碍程度" value={displayValue(disabilityLevel)} />
        <InfoBlock
          title="备注"
          value={displayValue(notes)}
          className="md:col-span-2 xl:col-span-3"
        />
      </div>

      <div className="border-t border-slate-100 pt-6">
        <h3 className="text-base font-semibold text-slate-800">家长 / 监护人信息</h3>
        {student.guardians.length > 0 ? (
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            {student.guardians.map((guardian, index) => (
              <article
                key={`${guardian.name}-${index}`}
                className="rounded-xl border border-slate-200 bg-slate-50 p-4"
              >
                <div className="flex items-baseline justify-between gap-4">
                  <h4 className="font-semibold text-slate-800">{displayValue(guardian.name)}</h4>
                  <span className="text-xs font-medium text-brand-dark">
                    {displayValue(guardian.relationship)}
                  </span>
                </div>
                <dl className="mt-4 grid gap-x-5 gap-y-3 text-sm sm:grid-cols-2">
                  <GuardianDetail label="电话" value={guardian.phone} />
                  <GuardianDetail label="邮箱" value={guardian.email} />
                  <GuardianDetail label="职业" value={guardian.occupation} />
                  <GuardianDetail label="住址" value={guardian.address} />
                </dl>
              </article>
            ))}
          </div>
        ) : (
          <p className="mt-3 text-sm text-slate-500">未填写监护人信息。</p>
        )}
      </div>
    </div>
  );
}

type StudentFieldChange = <Key extends keyof StudentFormValues>(
  key: Key,
  value: StudentFormValues[Key],
) => void;
type GuardianFieldChange = <Key extends keyof GuardianFormValue>(
  index: number,
  key: Key,
  value: GuardianFormValue[Key],
) => void;

function StudentInformationEditor({
  values,
  onFieldChange,
  onGuardianChange,
  onAddGuardian,
  onRemoveGuardian,
}: {
  values: StudentFormValues;
  onFieldChange: StudentFieldChange;
  onGuardianChange: GuardianFieldChange;
  onAddGuardian: () => void;
  onRemoveGuardian: (index: number) => void;
}): JSX.Element {
  const inputClassName =
    "min-h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20";

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <EditField label="姓名">
          <input
            required
            value={values.name}
            onChange={(event) => onFieldChange("name", event.target.value)}
            className={inputClassName}
          />
        </EditField>
        <EditField label="性别">
          <select
            value={values.gender}
            onChange={(event) =>
              onFieldChange("gender", event.target.value as StudentFormValues["gender"])
            }
            className={inputClassName}
          >
            <option value="unknown">未指定</option>
            <option value="male">男</option>
            <option value="female">女</option>
            <option value="other">其他</option>
          </select>
        </EditField>
        <EditField label="年龄">
          <input
            readOnly
            value={renderAge(values.birthdate)}
            className={`${inputClassName} cursor-not-allowed border-slate-200 bg-slate-100 text-slate-500`}
          />
        </EditField>
        <EditField label="出生日期">
          <input
            required
            type="date"
            value={values.birthdate}
            onChange={(event) => onFieldChange("birthdate", event.target.value)}
            className={inputClassName}
          />
        </EditField>
        <EditField label="学校">
          <input
            value={values.school}
            onChange={(event) => onFieldChange("school", event.target.value)}
            className={inputClassName}
          />
        </EditField>
        <EditField label="班级">
          <input
            value={values.classroom}
            onChange={(event) => onFieldChange("classroom", event.target.value)}
            className={inputClassName}
          />
        </EditField>
        <EditField label="小组">
          <input
            value={values.group}
            onChange={(event) => onFieldChange("group", event.target.value)}
            className={inputClassName}
          />
        </EditField>
        <EditField label="入学时间">
          <input
            type="month"
            value={values.enrollmentDate}
            onChange={(event) => onFieldChange("enrollmentDate", event.target.value)}
            className={inputClassName}
          />
        </EditField>
        <EditField label="障碍类别">
          <input
            value={values.disabilityCategory}
            onChange={(event) => onFieldChange("disabilityCategory", event.target.value)}
            className={inputClassName}
          />
        </EditField>
        <EditField label="障碍原因">
          <input
            value={values.disabilityCause}
            onChange={(event) => onFieldChange("disabilityCause", event.target.value)}
            className={inputClassName}
          />
        </EditField>
        <EditField label="障碍程度">
          <input
            value={values.disabilityLevel}
            onChange={(event) => onFieldChange("disabilityLevel", event.target.value)}
            className={inputClassName}
          />
        </EditField>
        <EditField label="备注" className="md:col-span-2 xl:col-span-3">
          <textarea
            rows={3}
            value={values.notes}
            onChange={(event) => onFieldChange("notes", event.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
          />
        </EditField>
      </div>

      <div className="border-t border-slate-100 pt-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-base font-semibold text-slate-800">家长 / 监护人信息</h3>
          <button
            type="button"
            onClick={onAddGuardian}
            className="inline-flex min-h-11 items-center justify-center rounded-lg border border-brand/40 bg-white px-4 text-sm font-semibold text-brand-dark hover:bg-brand/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
          >
            新增监护人
          </button>
        </div>
        <div className="mt-4 space-y-4">
          {values.guardians.map((guardian, index) => (
            <article key={index} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {GUARDIAN_FIELDS.map((field) => (
                  <EditField key={field.key} label={field.label}>
                    <input
                      type={
                        field.key === "email" ? "email" : field.key === "phone" ? "tel" : "text"
                      }
                      value={guardian[field.key]}
                      onChange={(event) => onGuardianChange(index, field.key, event.target.value)}
                      className={inputClassName}
                    />
                  </EditField>
                ))}
              </div>
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={() => onRemoveGuardian(index)}
                  className="inline-flex min-h-11 items-center rounded-lg px-3 text-sm font-semibold text-red-600 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                >
                  移除该监护人
                </button>
              </div>
            </article>
          ))}
          {values.guardians.length === 0 ? (
            <p className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5 text-sm text-slate-500">
              暂无监护人信息，可点击“新增监护人”补充。
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const GUARDIAN_FIELDS: Array<{ key: keyof GuardianFormValue; label: string }> = [
  { key: "name", label: "姓名" },
  { key: "relationship", label: "称谓" },
  { key: "phone", label: "电话" },
  { key: "email", label: "邮箱" },
  { key: "occupation", label: "职业" },
  { key: "address", label: "住址" },
];

function EditField({
  label,
  className = "",
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <label className={`block text-sm ${className}`}>
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </span>
      {children}
    </label>
  );
}

function GuardianDetail({ label, value }: { label: string; value?: string }): JSX.Element {
  return (
    <div>
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="mt-1 break-words text-slate-700">{displayValue(value)}</dd>
    </div>
  );
}

function StatusRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-slate-100 pb-3 last:border-0 last:pb-0">
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-semibold text-slate-800">{value}</dd>
    </div>
  );
}

function getRecommendationCopy(action: ReturnType<typeof studentRecommendedAction>): {
  title: string;
  description: string;
  actionLabel: string;
} {
  if (action.type === "continue_assessment") {
    const mode = assessmentEntryMode(action.assessment);
    return {
      title: `继续${entryModeLabel(mode)}`,
      description: `这份评估开始于 ${formatDate(action.assessment.startedAt)}，可以从已保存的进度继续。`,
      actionLabel: mode === "activity" ? "继续活动评估" : "继续评分",
    };
  }
  if (action.type === "view_report") {
    return {
      title: "查看最新评估结果",
      description: `${formatDate(action.assessment.submittedAt ?? action.assessment.startedAt)} 完成的${assessmentRecordLabel()}已可用于报告与 IEP。`,
      actionLabel: "查看报告",
    };
  }
  return {
    title: "开始首次评估",
    description: "尚无评估记录，可根据现场条件选择完整量表或活动评估。",
    actionLabel: "选择评估方式",
  };
}

function InfoBlock({
  title,
  value,
  description,
  className = "",
}: {
  title: string;
  value: string;
  description?: string;
  className?: string;
}): JSX.Element {
  return (
    <div className={`rounded-lg border border-slate-200 bg-slate-50 p-4 ${className}`}>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
      <p className="mt-2 text-sm font-medium text-slate-700">{value}</p>
      {description ? <p className="mt-1 text-xs text-slate-500">{description}</p> : null}
    </div>
  );
}

function Tag({
  children,
  type = "default",
}: {
  children: ReactNode;
  type?: "default" | "success" | "progress";
}): JSX.Element {
  const palette =
    type === "success"
      ? "bg-brand/10 text-brand-dark"
      : type === "progress"
        ? "bg-sky-100 text-sky-800"
        : "bg-slate-100 text-slate-600";
  return (
    <span className={`rounded-full px-3 py-1 text-xs font-medium ${palette}`}>{children}</span>
  );
}

function renderGender(gender: string): string {
  switch (gender) {
    case "male":
      return "男";
    case "female":
      return "女";
    default:
      return "其他";
  }
}

function renderAssessmentStatus(status: Assessment["status"]): string {
  return status === "completed" ? "已完成" : "进行中";
}

function completedEntryCount(assessment: Assessment): number {
  if (assessment.coverageEntryCodes) {
    return assessment.coverageEntryCodes.length;
  }
  if (assessment.scoreSummary?.domainScores) {
    return Object.values(assessment.scoreSummary.domainScores).reduce(
      (sum, item) => sum + (item.completed ?? 0),
      0,
    );
  }
  return 0;
}

function toStudentFormValues(student: Student): StudentFormValues {
  return {
    name: student.profile.name,
    gender: student.profile.gender,
    birthdate: toDateInputValue(student.profile.birthdate),
    school: student.enrollment.school,
    classroom: student.enrollment.classroom,
    group: student.enrollment.group ?? "",
    enrollmentDate: toMonthInputValue(student.enrollment.startDate),
    disabilityCategory: getMetadataString(student.metadata, "disabilityCategory"),
    disabilityCause: getMetadataString(student.metadata, "disabilityCause"),
    disabilityLevel: getMetadataString(student.metadata, "disabilityLevel"),
    guardians:
      student.guardians.length > 0
        ? student.guardians.map((guardian) => ({
            name: guardian.name,
            relationship: guardian.relationship,
            address: guardian.address ?? "",
            occupation: guardian.occupation ?? "",
            phone: guardian.phone ?? "",
            email: guardian.email ?? "",
          }))
        : [],
    notes: getMetadataString(student.metadata, "notes"),
  };
}

function toStudentPayload(values: StudentFormValues) {
  return {
    name: values.name.trim(),
    gender: values.gender,
    birthdate: values.birthdate,
    school: values.school.trim(),
    classroom: values.classroom.trim(),
    group: values.group.trim(),
    enrollmentDate: toApiDateFromMonth(values.enrollmentDate),
    disabilityCategory: values.disabilityCategory.trim(),
    disabilityCause: values.disabilityCause.trim(),
    disabilityLevel: values.disabilityLevel.trim(),
    guardians: values.guardians
      .map((guardian) => ({
        name: guardian.name.trim(),
        relationship: guardian.relationship.trim(),
        address: guardian.address.trim(),
        occupation: guardian.occupation.trim(),
        phone: guardian.phone.trim(),
        email: guardian.email.trim(),
      }))
      .filter((guardian) => guardian.name.length > 0),
    notes: values.notes.trim(),
  };
}

function createEmptyGuardian(): GuardianFormValue {
  return {
    name: "",
    relationship: "",
    address: "",
    occupation: "",
    phone: "",
    email: "",
  };
}

function getMetadataString(metadata: Student["metadata"], key: string): string {
  const value = metadata?.[key];
  return typeof value === "string" ? value : "";
}

function toDateInputValue(value: string | undefined): string {
  if (!value) return "";
  return /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : "";
}

function toMonthInputValue(value: string | undefined): string {
  if (!value) return "";
  return /^\d{4}-\d{2}/.test(value) ? value.slice(0, 7) : "";
}

function toApiDateFromMonth(value: string): string {
  if (!value) return "";
  return /^\d{4}-\d{2}$/.test(value) ? `${value}-01` : value;
}

function displayValue(value: string | undefined): string {
  return value?.trim() || "未填写";
}

function displayDate(value: string | undefined): string {
  return value ? formatDate(value) : "未填写";
}

function renderAge(birthdate: string): string {
  if (!birthdate) return "未填写";
  const birth = new Date(`${birthdate}T00:00:00`);
  if (Number.isNaN(birth.getTime())) return "未填写";
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const birthdayPassed =
    today.getMonth() > birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() >= birth.getDate());
  if (!birthdayPassed) age -= 1;
  return `${Math.max(age, 0)} 岁`;
}
